/**
 * Admin Utilities
 * 
 * Utility functions for checking admin status
 */

import { supabase } from '../lib/supabase';

/**
 * Check if the current user is an admin
 * 
 * Admin status is determined by:
 * 1. Role field in user_profiles table (primary method)
 * 2. Fallback to email-based check if user_profiles table doesn't exist or profile not found
 * 
 * @returns Promise<boolean> - True if user is an admin, false otherwise
 */
export async function isAdmin(): Promise<boolean> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return false;
    }

    // Primary method: Check role in user_profiles table (if it exists)
    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();
      
      // If query succeeded and role is admin, return true
      if (!profileError && profile?.role === 'admin') {
        return true;
      }
      
      // If table doesn't exist (error code 42P01), fall through to email check
      // If table exists but no profile found, also fall through to email check
      if (profileError && profileError.code !== '42P01' && profileError.code !== 'PGRST116') {
        // Some other error occurred, log it but continue to fallback
        console.warn('Error checking user_profiles, falling back to email check:', profileError);
      }
    } catch (profileError: any) {
      // If user_profiles table doesn't exist yet (relation does not exist), fall back to email check
      // This is expected if the migration hasn't been run yet
      if (profileError?.code === '42P01' || profileError?.message?.includes('does not exist')) {
        // Table doesn't exist - this is fine, we'll use email fallback
      } else {
        console.warn('Error checking user_profiles, falling back to email check:', profileError);
      }
    }

    // Fallback: Check if user email is in admin list (for backward compatibility)
    // This is useful if user_profiles table hasn't been created yet
    if (user.email) {
      const adminEmails = [
        'admin@moneymojo.com',
        'zolandor@moneymojo.com',
        'info@zolandor.com',
        // Add more admin emails as needed
      ];

      // Check exact email match
      if (adminEmails.includes(user.email.toLowerCase())) {
        return true;
      }

      // Check email domain (optional - for @moneymojo.com domain)
      const adminDomains = ['@moneymojo.com'];
      const emailDomain = '@' + user.email.split('@')[1]?.toLowerCase();
      if (adminDomains.includes(emailDomain)) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Get current user email
 * 
 * @returns Promise<string | null> - The authenticated user's email, or null if not authenticated
 */
export async function getCurrentUserEmail(): Promise<string | null> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user || !user.email) {
      return null;
    }

    return user.email;
  } catch (error) {
    console.error('Error getting current user email:', error);
    return null;
  }
}

